import React, { useEffect } from 'react';
import { Form, Input, InputNumber, message, Modal, Select, Switch, Tooltip, Typography } from 'antd';
import { createModelConfig, getModelConfigDetail, updateModelConfig, ModelConfigVo, PROVIDER_OPTIONS } from '@/api/modelConfig';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

// DashScope API 端点类型选项
const ENDPOINT_TYPE_OPTIONS = [
  { label: 'AUTO（自动检测）', value: 'AUTO' },
  { label: 'TEXT（强制文本 API）', value: 'TEXT' },
  { label: 'MULTIMODAL（强制多模态 API）', value: 'MULTIMODAL' },
];

export interface EditModelConfigProp {
  data?: ModelConfigVo;
  isEdit?: boolean;
  updateFn: () => void;
  onCancel: () => void;
}

const EditModelConfig = ({ data, isEdit, updateFn, onCancel }: EditModelConfigProp) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const provider = Form.useWatch('provider', form);

  useEffect(() => {
    if (isEdit && data?.id) {
      setLoading(true);
      getModelConfigDetail(data.id)
        .then(res => {
          form.setFieldsValue({
            ...res,
            enableThinking: res.enableThinking === 1,
          });
        })
        .finally(() => setLoading(false));
    }
  }, [data?.id, isEdit, form]);

  const onFinish = () => {
    form.validateFields().then(values => {
      const params = {
        ...values,
        id: isEdit ? data?.id : undefined,
        enableThinking: values.enableThinking ? 1 : 0,
      };
      const api = isEdit ? updateModelConfig : createModelConfig;
      api(params as any).then(() => {
        message.success('保存成功');
        onCancel();
        updateFn();
      });
    });
  };

  return (
    <Modal
      title={isEdit ? '编辑模型配置' : '新增模型配置'}
      open={true}
      onCancel={onCancel}
      onOk={onFinish}
      maskClosable={false}
      width={600}
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" initialValues={{ temperature: 0.7, maxTokens: 4096 }}>
        <Form.Item name="configName" label="配置名称" rules={[{ required: true, message: '请输入配置名称' }]}>
          <Input placeholder="请输入配置名称" />
        </Form.Item>
        <Form.Item name="provider" label="模型供应商" rules={[{ required: true, message: '请选择供应商' }]}>
          <Select options={PROVIDER_OPTIONS} placeholder="请选择供应商" />
        </Form.Item>
        <Form.Item name="modelName" label="模型名称" rules={[{ required: true, message: '请输入模型名称' }]}>
          <Input placeholder="如 qwen-plus, gpt-4, llama2" />
        </Form.Item>
        <Form.Item name="apiKeyRef" label="API Key 引用" rules={[{ required: true, message: '请输入 API Key 引用路径' }]}>
          <Input placeholder="密钥管理系统引用路径" />
        </Form.Item>
        <Form.Item name="baseUrl" label="自定义 Endpoint">
          <Input placeholder="可选，自定义 API 地址" />
        </Form.Item>
        {/* DashScope 专属配置：API 端点类型 */}
        {provider === 'dashscope' && (
          <Form.Item
            name="endpointType"
            label={
              <span>
                API 端点类型&nbsp;
                <Tooltip title={
                  <div>
                    <Text>DashScope API 端点类型说明：</Text>
                    <ul style={{ paddingLeft: 16, marginTop: 8 }}>
                      <li><Text strong>AUTO</Text>：自动检测（qwen3.5系列自动使用多模态 API）</li>
                      <li><Text strong>TEXT</Text>：强制使用文本生成 API，适用于非标准模型名称如 qwen3.5-35b-a3b</li>
                      <li><Text strong>MULTIMODAL</Text>：强制使用多模态 API，支持图像输入</li>
                    </ul>
                    <Text type="warning" style={{ marginTop: 8 }}>⚠️ qwen3.5-35b-a3b 等非标准命名的模型需设置为 TEXT，否则自动检测为多模态导致响应异常</Text>
                  </div>
                }>
                  <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </span>
            }
          >
            <Select options={ENDPOINT_TYPE_OPTIONS} placeholder="请选择 API 端点类型" />
          </Form.Item>
        )}
        <Form.Item name="temperature" label="Temperature" tooltip="控制输出随机性，0-2">
          <InputNumber min={0} max={2} step={0.1} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="maxTokens" label="Max Tokens" tooltip="最大输出长度">
          <InputNumber min={1} max={32000} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="topP" label="Top P" tooltip="核采样参数，0-1">
          <InputNumber min={0} max={1} step={0.1} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="topK" label="Top K" tooltip="top-k 采样参数">
          <InputNumber min={1} max={100} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="enableThinking" label="启用 Thinking 模式" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="thinkingBudget" label="Thinking 预算" tooltip="Thinking 过程最大 Token 消耗">
          <InputNumber min={1} max={10000} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="extraParams" label="扩展参数" tooltip="JSON 格式的额外参数">
          <Input.TextArea rows={3} placeholder='{"key": "value"}' />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditModelConfig;